import { BurnTemperatureSeriesNonSensitive } from "../../BurnTemperatureSeriesNonSensitive";
import { flueBurnMetricAggregateTimeUnits } from "../../../chimney/flue/metrics/FlueBurnMetricAggregateTimeUnit";
import React, { FC, useMemo } from "react";
import { SxProps, useTheme } from "@mui/material";
import { XAxis } from "@mui/x-charts-premium";
import { DateTime, Duration } from "luxon";
import {
  DurationSeriesMuiChart,
  DurationSeriesMuiChartProps,
} from "firefly-shared-ts-durationseries-mui-chart";
import { SizeClass } from "shared-ts-ui-responsive";
import { FlueBurnMetricAggregates } from "../../../chimney/flue/metrics/FlueBurnMetricAggregates";
import { useXAxis } from "./useXAxis";
import { useChartsReferenceLines } from "./useChartsReferenceLines";
import { useYAxis } from "./useYAxis";
import { useDurationSeries } from "./useDurationSeries";
import { QueryResultMetaData } from "shared-ts-ui-loading";
import { TimeLineAnnotationProps } from "shared-ts-mui";
import { useTranslation } from "react-i18next";
import { BurnEvent } from "../../BurnEvent";

const unit = "°C";

export interface BurnTimeSeriesAnonymizedChartProps {
  queryResultMetaData?: QueryResultMetaData;
  flueBurnMetricAggregates: FlueBurnMetricAggregates;
  burnEvents?: Array<BurnEvent[]>;
  burnTimeSeries?: Array<BurnTemperatureSeriesNonSensitive>;
  burnColors?: Array<string | undefined>;
  burnDisplayNames?: Array<string>;
  selectedMonth?: DateTime;
  multiSelect: boolean;
  timeAxis?: {
    minimumMaxDuration?: Duration | undefined;
    maxDuration?: Duration | undefined;
    zoom?: boolean;
  };
  hideLegend?: boolean;
  layout?: SizeClass;
  sx?: SxProps;
}

export const BurnTimeSeriesAnonymizedChart: FC<
  BurnTimeSeriesAnonymizedChartProps
> = (props) => {
  const {
    flueBurnMetricAggregates,
    burnTimeSeries,
    burnColors,
    burnDisplayNames,
    selectedMonth,
    multiSelect,
  } = props;
  const theme = useTheme();
  const { t } = useTranslation("firefly-shared-ts-domain");
  const flueMetricsSinceStart =
    flueBurnMetricAggregates.querySingleMetricsOrUndef(
      flueBurnMetricAggregateTimeUnits.ever
    );

  const maxTemperatureSinceStart = flueMetricsSinceStart?.values.temperatureMax;

  const maxY = useMemo(() => {
    return maxTemperatureSinceStart ?? 100;
  }, [maxTemperatureSinceStart]);

  const yAxis = useYAxis(maxY, multiSelect, burnTimeSeries);

  const { durationSeries, durationSeriesOptions } = useDurationSeries(
    burnTimeSeries,
    burnColors,
    burnDisplayNames,
    flueBurnMetricAggregates.flueId,
    unit
  );

  const referenceLines = useChartsReferenceLines(
    flueBurnMetricAggregates,
    durationSeries,
    selectedMonth
  );
  const xAxis: XAxis<"linear"> = useXAxis(props);
  const xAxisZoom = useMemo(() => {
    return {
      preferredTicks: 6,
    } as DurationSeriesMuiChartProps["xAxisZoom"];
  }, []);

  const annotations: Array<TimeLineAnnotationProps> | undefined =
    useMemo(() => {
      const events: Array<TimeLineAnnotationProps> = [];

      props.burnEvents?.forEach((it, index) => {
        it.forEach((event) => {
          events.push({
            seriesId: undefined,
            time: event.duration,
            label: t(`BurnEventType.${event.eventType}`),
            color: theme.palette.primary.main,
          });
        });
      });
      return events;
    }, [props.burnEvents, t, theme.palette.primary.main]);

  return (
    <DurationSeriesMuiChart
      durationSeries={durationSeries}
      durationSeriesOptions={durationSeriesOptions}
      yAxisConfig={yAxis}
      xAxisConfig={xAxis}
      xAxisZoom={xAxisZoom}
      referenceLines={referenceLines}
      annotations={annotations}
      hideLegend={props.hideLegend}
      sx={props.sx}
    />
  );
};
